import ModalsManagerContext, { defaultValue } from "contexts/ModalsManager";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { useContext, useEffect, useRef, useState } from "react";

import CalendarIcon from "assets/icons/calendar.svg?react";
import AddIcon from "assets/icons/hard_drive.svg?react";
import CaseIcon from "assets/icons/case.svg?react";
import EntityIcon from "assets/icons/desk_hover.svg?react";
import LetterIcon from "assets/icons/project.svg?react";
import InvoiceIcon from "assets/icons/credit_card.svg?react";

import SenderIcon from "assets/icons/sender.svg?react";
import PostageIcon from "assets/icons/scan.svg?react";
import TaskIcon from "assets/icons/task.svg?react";
import HelpdeskIcon from "assets/icons/chati.svg?react";
import ChatIcon from "assets/icons/chat_bubble.svg?react";

import ReactDOM from "react-dom";
import { useOnClickOutside } from "usehooks-ts";
import { Can } from "contexts/AbilityContext";
import { useTranslation } from "react-i18next";

const SidebarAddingOptions: FC<Props> = ({
  addButtonRef,
  toggle,
  portal = false,
}) => {
  const { t } = useTranslation();
  const { setModals, setOptions } = useContext(ModalsManagerContext);

  const handleClickDossier = () => {
    setOptions(null);
    setModals({ ...defaultValue, createDossier: true, hide: false });
    toggle(false);
  };

  const handleClickCase = () => {
    setOptions(null);
    setModals({ ...defaultValue, createCase: true, hide: false });
    toggle(false);
  };

  const handleClickEntity = () => {
    setModals({ ...defaultValue, createEntity: true, hide: false });
    toggle(false);
  };

  const handleClickLetter = () => {
    setModals({ ...defaultValue, createLetter: true, hide: false });
    toggle(false);
  };

  const handleClickPostage = () => {
    setModals({ ...defaultValue, createPostage: true, hide: false });
    toggle(false);
  };

  const handleClickTask = () => {
    setModals({ ...defaultValue, createTask: true, hide: false });
    toggle(false);
  };

  const handleClickEvent = () => {
    setModals({ ...defaultValue, createEvent: true, hide: false });
    toggle(false);
  };

  const handleClickHelpdesk = () => {
    setModals({ ...defaultValue, createHelpdesk: true, hide: false });
    toggle(false);
  };

  const handleClickChat = () => {
    setModals({ ...defaultValue, createChat: true, hide: false });
    toggle(false);
  };

  const handleClickInvoice = () => {
    setModals({ ...defaultValue, createInvoice: true, hide: false });
    toggle(false);
  };

  const handleClickEngagement = () => {
    setOptions(null);
    setModals({ ...defaultValue, createBudgetEngagement: true, hide: false });
    toggle(false);
  };

  const handleClickInvoiceRegistryEntry = () => {
    setOptions(null);
    setModals({ ...defaultValue, createInvoiceRegistryEntry: true, hide: false });
    toggle(false);
  };

  const handleClickBudgetExpense = () => {
    setOptions(null);
    setModals({ ...defaultValue, createBudgetExpense: true, hide: false });
    toggle(false);
  };

  const handleClickPost = () => {
    sessionStorage.removeItem("reply");
    sessionStorage.removeItem("msg");
    sessionStorage.removeItem("sendTo");
    sessionStorage.removeItem("type");
    setModals({ ...defaultValue, createPost: true, hide: false });
    toggle(false);
  };

  const position = addButtonRef?.current?.getBoundingClientRect();

  const outsideRef = useRef(null);

  useOnClickOutside(outsideRef, () => {
    toggle(false);
  });

  const [showed, setShowed] = useState(false);

  useEffect(() => {
    setShowed(true);
  }, []);

  const minWidth = 280;

  const content = () => {
    return (
      <div className={styles.wrapper}>
        <div onClick={handleClickPost} className={styles.box}>
          <SenderIcon />
          {t("menu.parcel")}
        </div>
        <Can I="accessModule" an="workspace">
          <div onClick={handleClickChat} className={styles.box}>
            <ChatIcon />
            {t("navigation.chat")}
          </div>
        </Can>
        <div onClick={handleClickPostage} className={styles.box}>
          <PostageIcon />
          {t("navigation.letter")}
        </div>
        <Can I="accessModule" a="cases">
          <>
            <div className={styles.line}></div>
            <div onClick={handleClickCase} className={styles.box}>
              <CaseIcon />
              {t("cases.case")}
            </div>
            <div className={styles.line}></div>
          </>
        </Can>
        <Can I="accessModule" a="word">
          <div onClick={handleClickLetter} className={styles.box}>
            <LetterIcon />
            {t("sidebar.document")}
          </div>
        </Can>
        <Can I="accessModule" a="drive">
          <div onClick={handleClickDossier} className={styles.box}>
            <AddIcon style={{ paddingLeft: "3px" }} />
            {t("menu.fileOrLink")}
          </div>
        </Can>
        <div onClick={handleClickEntity} className={styles.box}>
          <EntityIcon />
          {t("columns.entity")}
        </div>

        <Can I="accessModule" a="tasks">
          <>
            <div className={styles.line}></div>

            <div onClick={handleClickEvent} className={styles.box}>
              <CalendarIcon />
              {t("menu.event")}
            </div>
            <div onClick={handleClickTask} className={styles.box}>
              <TaskIcon />
              {t("menu.task")}
            </div>
          </>
        </Can>

        <div onClick={handleClickHelpdesk} className={styles.box}>
          <HelpdeskIcon />
          {t("helpdesk.title")}
        </div>

        <div className={styles.line}></div>

        <div onClick={handleClickBudgetExpense} className={styles.box}>
          <InvoiceIcon />
          Wydatek budżetowy
        </div>

        <Can I="accessModule" a="admin">
          <>
            <div onClick={handleClickEngagement} className={styles.box}>
              <InvoiceIcon />
              Zaangażowanie
            </div>

            <div onClick={handleClickInvoiceRegistryEntry} className={styles.box}>
              <InvoiceIcon />
              Faktura/Rachunek
            </div>

            <div onClick={handleClickInvoice} className={styles.box}>
              <InvoiceIcon />
              {t("sidebar.invoice")}
            </div>
          </>
        </Can>
      </div>
    );
  };

  if (!portal) return content();

  return ReactDOM.createPortal(
    <div
      ref={outsideRef}
      style={{
        width: showed
          ? position.width < minWidth
            ? minWidth
            : position.width
          : "0px",
        left: position.left,
        top: position.top + position.height + 5,
      }}
      className={`${styles.menu} ${showed && styles.open}`}
    >
      {content()}
    </div>,
    document.body
  );
};

export default SidebarAddingOptions;
